/**
 * Paginación server-side del Action Center Premium (0669).
 *
 * Controles inferiores: « ‹ N de M › » + selector de pageSize +
 * contador total de resultados. i18n en `t.sign.actionCenter.premium.paginacion*`.
 */

import { useI18n } from '../../../../i18n';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface ActionCenterPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const SVG_PREV = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M10 4L6 8l4 4" />
  </svg>
);

const SVG_NEXT = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M6 4l4 4-4 4" />
  </svg>
);

export function ActionCenterPagination(props: ActionCenterPaginationProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium;

  const totalPages = Math.max(1, Math.ceil(props.total / props.pageSize));
  const page = Math.min(Math.max(1, props.page), totalPages);
  const isFirst = page <= 1;
  const isLast = page >= totalPages;

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'DM Sans, sans-serif',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    background: '#fff',
    color: '#1F2937',
    cursor: 'pointer',
  };
  const btnDisabled: React.CSSProperties = {
    ...btnBase,
    color: '#9CA3AF',
    cursor: 'not-allowed',
    background: '#F9FAFB',
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 24px',
        borderTop: '1px solid #E5E7EB',
        background: '#FAFAFA',
        gap: 16,
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      {/* Selector de pageSize */}
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4B5563' }}
      >
        <span style={{ fontWeight: 600 }}>{tt.paginacionPageSize}</span>
        <select
          value={props.pageSize}
          onChange={(e) => props.onPageSizeChange(Number(e.target.value))}
          style={{
            padding: '4px 8px',
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            background: '#fff',
            fontSize: 12,
            fontFamily: 'DM Sans, sans-serif',
            color: '#1F2937',
            cursor: 'pointer',
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      {/* Total */}
      <span style={{ fontSize: 12, color: '#6B7280' }}>
        {tt.paginacionTotal.replace('{n}', String(props.total))}
      </span>

      {/* Controles « ‹ N de M › » */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={() => props.onPageChange(page - 1)}
          disabled={isFirst}
          style={isFirst ? btnDisabled : btnBase}
          aria-label={tt.paginacionAnterior}
        >
          {SVG_PREV}
          {tt.paginacionAnterior}
        </button>
        <span style={{ fontSize: 12, color: '#1F2937', minWidth: 110, textAlign: 'center' }}>
          {tt.paginacionPagina.replace('{n}', String(page)).replace('{total}', String(totalPages))}
        </span>
        <button
          type="button"
          onClick={() => props.onPageChange(page + 1)}
          disabled={isLast}
          style={isLast ? btnDisabled : btnBase}
          aria-label={tt.paginacionSiguiente}
        >
          {tt.paginacionSiguiente}
          {SVG_NEXT}
        </button>
      </div>
    </div>
  );
}
